import { useEffect, useState } from 'react';

import { useAuthContext, type OtpTarget } from '@/src/lib/authContext';

/**
 * useResendCountdown — the "Resend (in N seconds)" timer for an OTP screen.
 *
 * A TIMER ONLY. It does not send anything. The screen owns the `authApi.resendOtp`
 * call, because a resend also has to store the replacement challenge id and
 * decide where its errors go — screen business, not the clock's. The screen calls
 * `markSent()` once the network call has actually succeeded, which is what
 * restarts the countdown.
 *
 * ── Why the stamp lives in the draft, not here ──
 * The OTP screens let the user go BACK to edit the mobile/email and return. On
 * return the countdown must behave like a real backend would:
 *   - value UNCHANGED → no new code was sent → RESUME the running countdown.
 *   - value CHANGED   → a new code was sent  → it restarts (the issuing screen
 *     stamps a fresh send when its API call succeeds).
 * So the timer can't be plain per-screen state (that resets on every mount). It's
 * anchored to a timestamp kept in the transient auth draft, keyed by target — see
 * [[authDraft]]. `remaining` is DERIVED (not stored) from that timestamp against
 * a ticking `now`, which stays correct across the unmount/remount a
 * back-navigation causes.
 *
 * ── This deliberately no longer stamps on mount ──
 * It used to stamp a send itself when it found none for the current value, which
 * was right when the OTP was a mock and "arriving at the screen" WAS the send.
 * Now a code is dispatched by a real request, and only the screen that made it
 * knows it succeeded. If no stamp exists for the current value, that means no
 * code is known to have gone out — so the button is live IMMEDIATELY rather than
 * counting down from a send that never happened. That's the honest default: the
 * worst case is the user can request a code straight away.
 *
 * @param target       which OTP this screen verifies ('mobile' | 'email')
 * @param currentValue the value the code goes to right now (draft.mobile / .email)
 */
const DURATION = 20;

export function useResendCountdown(
  target: OtpTarget,
  currentValue: string
): { remaining: number; canResend: boolean } {
  const { otpSend } = useAuthContext();
  const record = otpSend[target];
  // A send counts only if it was stamped for the value showing now. Otherwise the
  // value was edited upstream (or nothing has been sent yet) and there's no
  // countdown to serve.
  const isCurrent = record?.sentForValue === currentValue;

  // A plain clock: `now` advances once a second so the derived `remaining`
  // recomputes. We store the clock, never the countdown itself — no setState of
  // derived state inside an effect.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Derived every render. No stamp for this value → nothing to wait on → 0.
  const remaining = isCurrent
    ? Math.max(0, DURATION - Math.floor((now - record.startedAt) / 1000))
    : 0;

  return { remaining, canResend: remaining <= 0 };
}
